import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Anchor, Avatar, Badge, Button, Group, Menu, Select, Stack, Text, Tooltip } from '@mantine/core';
import { useContextMenu } from '../ui/ContextMenu';
import { Section, initials } from '../ui/Section';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ROLE_LABELS } from '@pst/shared';
import { ApiError, api } from '../api';
import { InviteModal } from './InviteModal';
import { useCan, useMe } from './useMe';

/**
 * Who works on this project. Members see the list; Projektleitung and
 * Administration add and remove. Only the Administration invites people who
 * have no account yet.
 */
export function ProjectTeamPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const me = useMe();
  const canEdit = useCan('projects.write');
  const canInvite = useCan('users.manage');
  const queryClient = useQueryClient();
  const [inviteOpen, inviteDialog] = useDisclosure();
  const [candidate, setCandidate] = useState<string | null>(null);

  const members = useQuery({ queryKey: ['members', projectId], queryFn: () => api.members(projectId) });
  // Mail opens the user's own mail program (mailto:), with the project as subject.
  const project = queryClient.getQueryData<{ id: string; number: string; name: string }[]>(['projects'])?.find((p) => p.id === projectId);
  const mailto = (to: string[]): string => `mailto:${to.join(',')}${project ? `?subject=${encodeURIComponent(`[${project.number}] ${project.name}`)}` : ''}`;
  const others = (members.data ?? []).filter((m) => m.userId !== me?.id);
  const candidates = useQuery({
    queryKey: ['members', projectId, 'candidates'],
    queryFn: () => api.memberCandidates(projectId),
    enabled: canEdit,
  });

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['members', projectId] });
  const add = useMutation({
    mutationFn: (userId: string) => api.addMember(projectId, userId),
    onSuccess: () => setCandidate(null),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.removeMember(projectId, userId),
    onSettled: refresh,
  });
  const error = add.error ?? remove.error;
  const context = useContextMenu<{ userId: string; displayName: string; email: string }>((member, close) => {
    const self = member.userId === me?.id;
    return (
      <>
        <Menu.Label>{member.displayName}</Menu.Label>
        <Menu.Item component="a" href={mailto([member.email])} onClick={close}>E-Mail schreiben</Menu.Item>
        {canEdit && (!self || me?.role === 'admin') && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={() => { close(); remove.mutate(member.userId); }}>Aus dem Projekt entfernen</Menu.Item>
          </>
        )}
      </>
    );
  });

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
    <Stack gap="md" pb="xl">
      {error && (
        <Alert color="red" withCloseButton onClose={() => { add.reset(); remove.reset(); }}>
          {error instanceof ApiError ? error.message : 'Die Änderung ist fehlgeschlagen.'}
        </Alert>
      )}

      <Section
        title={`Team (${members.data?.length ?? 0})`}
        description="Wer hier steht, sieht das Projekt. Die Administration sieht jedes Projekt, auch ohne Eintrag."
        actions={
          <>
            {others.length > 0 && (
              <Button size="xs" variant="light" component="a" href={mailto(others.map((m) => m.email))} leftSection={<span aria-hidden>✉</span>}>
                E-Mail an das Team
              </Button>
            )}
            {canInvite && <Button size="xs" variant="default" onClick={inviteDialog.open}>Neue Person einladen</Button>}
          </>
        }
      >
        {members.isSuccess && members.data.length === 0 && (
          <Text size="sm" c="dimmed">Noch niemand zugeordnet.</Text>
        )}
        <Stack gap={0}>
          {(members.data ?? []).map((member, index) => {
            const self = member.userId === me?.id;
            const removable = canEdit && (!self || me?.role === 'admin');
            return (
              <Group
                key={member.userId}
                gap="sm"
                py="xs"
                wrap="nowrap"
                onContextMenu={(event) => context.open(event, member)}
                style={index === 0 ? undefined : { borderTop: '1px solid var(--mantine-color-default-border)' }}
              >
                {canEdit && (
                  <div style={{ width: 28, flex: '0 0 auto' }}>
                    {removable && (
                      <Tooltip label="Aus dem Projekt entfernen">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label={`${member.displayName} aus dem Projekt entfernen`}
                          onClick={() => remove.mutate(member.userId)}
                        >
                          {'✕'}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </div>
                )}
                <Avatar radius="xl" size={36} variant="light">{initials(member.displayName)}</Avatar>
                <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500} truncate>{member.displayName}</Text>
                    {self && <Badge size="xs" variant="outline" color="gray">Sie</Badge>}
                  </Group>
                  <Anchor size="xs" c="dimmed" truncate href={mailto([member.email])} title={`E-Mail an ${member.displayName}`}>{member.email}</Anchor>
                </Stack>
                <Badge variant="light" color="gray" radius="sm">{ROLE_LABELS[member.role]}</Badge>
                <Tooltip label={`E-Mail an ${member.displayName}`}>
                  <ActionIcon variant="light" size="md" component="a" href={mailto([member.email])} aria-label={`E-Mail an ${member.displayName}`}>✉</ActionIcon>
                </Tooltip>
              </Group>
            );
          })}
        </Stack>

        {canEdit && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (candidate) add.mutate(candidate);
            }}
          >
            <Group gap="xs" align="flex-end" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
              <Select
                label="Person hinzufügen"
                placeholder={candidates.data?.length === 0 ? 'Alle Konten sind bereits im Team' : 'Konto wählen'}
                searchable
                disabled={candidates.data?.length === 0}
                data={(candidates.data ?? []).map((entry) => ({
                  value: entry.userId,
                  label: `${entry.displayName} · ${ROLE_LABELS[entry.role]}`,
                }))}
                value={candidate}
                onChange={setCandidate}
                style={{ flex: 1, maxWidth: 380 }}
              />
              <Button type="submit" disabled={!candidate} loading={add.isPending}>Hinzufügen</Button>
            </Group>
          </form>
        )}
      </Section>

      {canInvite && <InviteModal opened={inviteOpen} onClose={inviteDialog.close} projectId={projectId} />}
      {context.menu}
    </Stack>
    </div>
  );
}
